/**
 * MobileParamsPage - Admin page to configure mobile app parameters
 */

import { useEffect, useState } from "react";
import {
  Card,
  Form,
  InputNumber,
  Button,
  Typography,
  Space,
  App,
  Skeleton,
  Descriptions,
  Switch,
  Divider,
} from "antd";
import {
  getMobileConfig,
  updateMobileConfig,
  getDefaultMobileConfig,
} from "@/services/configService";
import { forceUpdateAllDevices } from "@/services/viewService";
import { DEFAULT_OFFLINE_GRACE_SECONDS } from "@/hooks/useConnectionStatus";
import type { MobileConfig } from "@/types";

const { Title, Text } = Typography;

export default function MobileParamsPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<MobileConfig | null>(null);
  const [forceUpdating, setForceUpdating] = useState(false);
  const { modal, message } = App.useApp();

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await getMobileConfig();
      setConfig(data);
      form.setFieldsValue({
        heartbeatIntervalSeconds: data.heartbeatIntervalSeconds,
        survivalBatteryPercent: data.survivalBatteryPercent,
        storageThresholdMB: data.storageThresholdMB,
        offlineGraceSeconds: data.offlineGraceSeconds ?? DEFAULT_OFFLINE_GRACE_SECONDS,
        missedCaptureToleranceSeconds: data.missedCaptureToleranceSeconds ?? 60,
        // OTA settings
        otaEnabled: data.otaEnabled ?? false,
        otaAutoUpdateKiosk: data.otaAutoUpdateKiosk ?? false,
      });
    } catch (error) {
      console.error("Failed to load config:", error);
      message.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: {
    heartbeatIntervalSeconds: number;
    survivalBatteryPercent: number;
    storageThresholdMB: number;
    offlineGraceSeconds: number;
    missedCaptureToleranceSeconds: number;
    otaEnabled: boolean;
    otaAutoUpdateKiosk: boolean;
  }) => {
    try {
      setSaving(true);
      await updateMobileConfig(values);
      message.success("Configuration saved successfully");
      await loadConfig(); // Reload to get updated timestamp
    } catch (error) {
      console.error("Failed to save config:", error);
      message.error("Failed to save configuration. Make sure you have admin privileges.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaults = getDefaultMobileConfig();
    form.setFieldsValue(defaults);
  };

  const handleForceUpdateAll = () => {
    modal.confirm({
      title: 'Force Update All Devices',
      content: 'This will send an update command to every device. Production installations will install the update silently. Test installations will ignore the command.',
      okText: 'Force Update All',
      okType: 'danger',
      onOk: async () => {
        setForceUpdating(true);
        try {
          const count = await forceUpdateAllDevices();
          message.success(`Update command sent to ${count} device${count !== 1 ? 's' : ''}`);
        } catch {
          message.error('Failed to send update command');
        } finally {
          setForceUpdating(false);
        }
      },
    });
  };

  if (loading) {
    return <Skeleton active title={false} paragraph={{ rows: 8 }} />;
  }

  return (
    <div>
      <Title level={3}>Mobile Parameters</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Configure global parameters for the Android app. Changes take effect immediately
        for all connected devices.
      </Text>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ maxWidth: 500 }}
        >
          <Form.Item
            name="heartbeatIntervalSeconds"
            label="Heartbeat Interval (seconds)"
            rules={[
              { required: true, message: "Required" },
              { type: "number", min: 10, message: "Minimum 10 seconds" },
              { type: "number", max: 3600, message: "Maximum 3600 seconds (1 hour)" },
            ]}
            tooltip="How often the Android app sends status updates to the server"
          >
            <InputNumber
              min={10}
              max={3600}
              style={{ width: "100%" }}
              addonAfter="seconds"
            />
          </Form.Item>

          <Form.Item
            name="survivalBatteryPercent"
            label="Survival Battery Threshold (%)"
            rules={[
              { required: true, message: "Required" },
              { type: "number", min: 0, message: "Minimum 0%" },
              { type: "number", max: 80, message: "Maximum 80%" },
            ]}
            tooltip="When charger disconnects and battery is below this level, the app stops all activity and enters deep sleep to survive power outages. Set to 0 to disable."
          >
            <InputNumber
              min={0}
              max={80}
              style={{ width: "100%" }}
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item
            name="storageThresholdMB"
            label="Storage Threshold (MB)"
            rules={[
              { required: true, message: "Required" },
              { type: "number", min: 50, message: "Minimum 50 MB" },
              { type: "number", max: 5000, message: "Maximum 5000 MB" },
            ]}
            tooltip="Capturing will pause when free storage drops below this level"
          >
            <InputNumber
              min={50}
              max={5000}
              style={{ width: "100%" }}
              addonAfter="MB"
            />
          </Form.Item>

          <Form.Item
            name="offlineGraceSeconds"
            label="Offline Grace"
            rules={[
              { required: true, message: "Required" },
              { type: "number", min: 5, message: "Minimum 5" },
              { type: "number", max: 300, message: "Maximum 300" },
            ]}
            tooltip="Extra seconds allowed past the expected heartbeat before a device is shown offline. Threshold = heartbeat + grace."
          >
            <InputNumber
              min={5}
              max={300}
              step={5}
              style={{ width: "100%" }}
              addonAfter="s"
            />
          </Form.Item>

          <Form.Item
            name="missedCaptureToleranceSeconds"
            label="Missed Capture Tolerance (seconds)"
            rules={[
              { required: true, message: "Required" },
              { type: "number", min: 10, message: "Minimum 10 seconds" },
              { type: "number", max: 600, message: "Maximum 600 seconds (10 min)" },
            ]}
            tooltip="Time to wait after expected capture before marking it as 'missed' (vs 'pending')"
          >
            <InputNumber
              min={10}
              max={600}
              style={{ width: "100%" }}
              addonAfter="seconds"
            />
          </Form.Item>

          <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Latest Published Version">
              {config?.latestAppVersionCode && config.latestAppVersionCode > 0 ? (
                <span>
                  <strong>v1.0.{config.latestAppVersionCode}</strong>
                  <span style={{ color: '#888', marginLeft: 8 }}>(code: {config.latestAppVersionCode})</span>
                </span>
              ) : (
                <span style={{ color: '#888' }}>Not set — run deploy-playstore.sh to publish</span>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Divider>OTA Updates</Divider>

          <Form.Item
            name="otaEnabled"
            label="Enable OTA Updates"
            valuePropName="checked"
            tooltip="Allow devices to receive updates via OTA (Over-The-Air) instead of Play Store"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="otaAutoUpdateKiosk"
            label="Auto-update Production Installations"
            valuePropName="checked"
            tooltip="Production Installations will automatically install updates when a new OTA version is published. Test Installations still require manual confirmation."
          >
            <Switch />
          </Form.Item>

          {config?.otaVersionCode && config.otaVersionCode > 0 && (
            <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="OTA Version Available">
                <span>
                  <strong>v1.0.{config.otaVersionCode}</strong>
                  <span style={{ color: '#888', marginLeft: 8 }}>(code: {config.otaVersionCode})</span>
                </span>
              </Descriptions.Item>
            </Descriptions>
          )}

          <Button
            danger
            loading={forceUpdating}
            onClick={handleForceUpdateAll}
            style={{ marginBottom: 16 }}
          >
            Force Update All Devices
          </Button>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                Save Changes
              </Button>
              <Button onClick={handleReset}>Reset to Defaults</Button>
            </Space>
          </Form.Item>
        </Form>

        {config?.updatedAt && (
          <Descriptions
            style={{ marginTop: 24 }}
            size="small"
            column={1}
            bordered
          >
            <Descriptions.Item label="Last Updated">
              {config.updatedAt.toDate().toLocaleString()}
            </Descriptions.Item>
            {config.updatedBy && (
              <Descriptions.Item label="Updated By">
                {config.updatedBy}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Card>
    </div>
  );
}
